import { ConcessionsAndWaiversScheme } from '../types';

type PropertyMapping = [string, string]; // [dataKey, headerLabel]

const CONCESSION_WAIVER_PROPS: PropertyMapping[] = [
  ['description', 'Description'],
  ['amount', 'Amount'],
  ['status', 'Status']
];

const ADDITIONAL_PROPS: PropertyMapping[] = [
  ['description', 'Description'],
  ['amount', 'Amount'],
  ['status', 'Status'],
  ['type', 'Type'],
  ['appliedOn', 'Applied On']
];

const buildIndexedCols = (
  items: Record<string, any>[],
  maxCount: number,
  categoryPrefix: string,
  properties: PropertyMapping[]
): Record<string, any> => {
  const cols: Record<string, any> = {};
  for (let i = 0; i < maxCount; i++) {
    const item = items[i];
    properties.forEach(([key, label]) => {
      cols[`${categoryPrefix} ${i + 1} - ${label}`] = item?.[key] ?? '';
    });
  }
  return cols;
};

const transformConcessionsAndWaiversData = (
  data: ConcessionsAndWaiversScheme[]
): any[] => {
  const maxConcessions = data.reduce(
    (max, s) => Math.max(max, s.concessions.length),
    0
  );
  const maxWaivers = data.reduce(
    (max, s) => Math.max(max, s.waivers.length),
    0
  );
  const maxAdditional = data.reduce(
    (max, s) => Math.max(max, (s.additionalConcessionsAndWaivers || []).length),
    0
  );

  return data.map((scheme, idx) => {
    const approvedConcessionTotal = scheme.concessions
      .filter((c) => c.status === 'APPROVED')
      .reduce((sum, c) => sum + (c.amount || 0), 0);
    const approvedWaiverTotal = scheme.waivers
      .filter((w) => w.status === 'APPROVED')
      .reduce((sum, w) => sum + (w.amount || 0), 0);
    const expectedAmount =
      scheme.amount + approvedConcessionTotal + approvedWaiverTotal;

    const staticCols = {
      id: idx + 1,
      'Student Name': scheme.studentDetails?.name || '',
      'Enrollment No': scheme.studentDetails?.enrollmentNo || '',
      Status: scheme.studentDetails?.status || '',
      Particulars: scheme.particulars || '',
      'Fee Period': scheme.feePeriod || '',
      'New Admission': scheme.isNewAdmission ? 'Yes' : 'No',
      'Gross Amount': expectedAmount,
      Amount: scheme.amount,
      'Late Fee': scheme.lateFee || 0,
      Scheme: scheme.scheme || '',
      Grade: scheme.grade || '',
      Division: scheme.division || '',
      Session: scheme.session || '',
      Component: scheme.component || ''
    };

    const concessionCols = buildIndexedCols(
      scheme.concessions,
      maxConcessions,
      'Concession',
      CONCESSION_WAIVER_PROPS
    );
    const waiverCols = buildIndexedCols(
      scheme.waivers,
      maxWaivers,
      'Waiver',
      CONCESSION_WAIVER_PROPS
    );
    const additionalCols = buildIndexedCols(
      scheme.additionalConcessionsAndWaivers || [],
      maxAdditional,
      'Additional Concession/Waiver',
      ADDITIONAL_PROPS
    );

    return {
      ...staticCols,
      ...concessionCols,
      ...waiverCols,
      ...additionalCols
    };
  });
};

export { transformConcessionsAndWaiversData };
